import { useState } from "react"
import { Search, Download, SlidersHorizontal, Plus } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "@/components/ui/sheet"
import { Label } from "@/components/ui/label"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Patient } from "../-data/fixtures"

interface PatientsTableProps {
  patients: Patient[]
  onAddPatient: (patient: Patient) => void
}

export function PatientsTable({ patients, onAddPatient }: PatientsTableProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [sortConfig, setSortConfig] = useState<{ key: keyof Patient; direction: 'asc' | 'desc' } | null>(null)
  const [newPatient, setNewPatient] = useState<Partial<Patient>>({
    name: "",
    email: "",
    insurance: "",
    phoneNumber: "",
    emiratesId: "",
    queue: "",
    lastVisit: new Date().toISOString().split('T')[0],
    status: "Active"
  })
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const handleSort = (key: keyof Patient) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  const filteredPatients = [...patients]
    .filter(patient => 
      patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.emiratesId.includes(searchTerm)
    )
    .sort((a, b) => {
      if (!sortConfig) return 0
      const aValue = a[sortConfig.key]
      const bValue = b[sortConfig.key]
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (newPatient.name && newPatient.email) {
        onAddPatient({
            ...newPatient,
            id: Math.random().toString(36).substr(2, 9),
            status: "Active"
        } as Patient)
        setIsSheetOpen(false)
        setNewPatient({
            name: "",
            email: "",
            insurance: "",
            phoneNumber: "",
            emiratesId: "",
            queue: "",
            lastVisit: new Date().toISOString().split('T')[0],
            status: "Active"
        })
    }
  }

  return (
    <Card className="rounded-xl shadow-sm border bg-white mt-6">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center gap-2">
          <CardTitle className="text-xl font-semibold">Patient List</CardTitle>
          <Badge variant="secondary" className="rounded-full bg-gray-100 text-gray-600 hover:bg-gray-100">
            {filteredPatients.length} Active
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search patients..."
              className="pl-9 h-9 bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button variant="outline" size="icon" className="h-9 w-9">
            <Download className="h-4 w-4" />
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-2">
                <SlidersHorizontal className="h-4 w-4" />
                Sort
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Sort by</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleSort('name')}>
                Name {sortConfig?.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSort('lastVisit')}>
                Last Visit {sortConfig?.key === 'lastVisit' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSort('queue')}>
                Queue {sortConfig?.key === 'queue' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-2">
                <Plus className="h-4 w-4" />
                Add Patient
              </Button>
            </SheetTrigger>
            <SheetContent className="overflow-y-auto">
              <SheetHeader>
                <SheetTitle>Add New Patient</SheetTitle>
                <SheetDescription>
                  Enter the details of the new patient here. Click save when you're done.
                </SheetDescription>
              </SheetHeader>
              <form onSubmit={handleSubmit} className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Name</Label>
                  <Input 
                    id="name" 
                    value={newPatient.name} 
                    onChange={(e) => setNewPatient({...newPatient, name: e.target.value})}
                    required 
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    value={newPatient.email} 
                    onChange={(e) => setNewPatient({...newPatient, email: e.target.value})}
                    required 
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="insurance">Insurance</Label>
                  <Input 
                    id="insurance" 
                    value={newPatient.insurance} 
                    onChange={(e) => setNewPatient({...newPatient, insurance: e.target.value})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input 
                    id="phone" 
                    value={newPatient.phoneNumber} 
                    onChange={(e) => setNewPatient({...newPatient, phoneNumber: e.target.value})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="emiratesId">Emirates ID</Label>
                  <Input 
                    id="emiratesId" 
                    value={newPatient.emiratesId} 
                    onChange={(e) => setNewPatient({...newPatient, emiratesId: e.target.value})}
                  />
                </div>
                 <div className="grid gap-2">
                  <Label htmlFor="queue">Queue</Label>
                  <Input 
                    id="queue" 
                    value={newPatient.queue} 
                    onChange={(e) => setNewPatient({...newPatient, queue: e.target.value})}
                  />
                </div>
                <SheetFooter>
                  <Button type="submit">Save changes</Button>
                </SheetFooter>
              </form>
            </SheetContent>
          </Sheet>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[250px] cursor-pointer" onClick={() => handleSort('name')}>
                Patient {sortConfig?.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead>Insurance</TableHead>
              <TableHead>Phone Number</TableHead>
              <TableHead>Emirates ID</TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('queue')}>
                 Patient Queue {sortConfig?.key === 'queue' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead className="text-right cursor-pointer" onClick={() => handleSort('lastVisit')}>
                Last Visit {sortConfig?.key === 'lastVisit' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredPatients.map((patient) => (
              <TableRow key={patient.id} className="hover:bg-gray-50/50">
                <TableCell className="font-medium">
                  <div>
                    <div className="font-semibold text-sm">{patient.name}</div>
                    <div className="text-xs text-muted-foreground">{patient.email}</div>
                  </div>
                </TableCell>
                <TableCell>{patient.insurance}</TableCell>
                <TableCell>{patient.phoneNumber}</TableCell>
                <TableCell>{patient.emiratesId}</TableCell>
                <TableCell>{patient.queue}</TableCell>
                <TableCell className="text-right">{patient.lastVisit}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
